import { RequestCompanySchema } from '@company/schemas/RequestCompanySchema';
import { useGetCompanyTypesAllowedQuery } from '@company/services/company';
import { useCreateCompanyRequestMutation } from '@company/services/companyRequest';
import { ICompanySubmitRequest } from '@company/types/company';
import { getReasonMessage } from '@company/utils/company';
import { Autocomplete, Button, styled, TextField, Tooltip, tooltipClasses, TooltipProps } from '@mui/material';
import { useGetSalesManagersQuery } from '@services/accounts';
import { useGetCountriesQuery } from '@services/countries';
import { handleError } from '@utils/common';
import { useFormik } from 'formik';
import { useMemo } from 'react';

const CustomWidthTooltip = styled(({ className, ...props }: TooltipProps) => (
    <Tooltip {...props} classes={{ popper: className }} />
))({
    [`& .${tooltipClasses.tooltip}`]: {
        minWidth: 200,
        textAlign: 'center',
    },
});

const OverviewForm = () => {
    const { data: countries, isLoading: isCountriesLoading } = useGetCountriesQuery();
    const { data: companyTypes, isLoading: isCompanyTypesLoading } = useGetCompanyTypesAllowedQuery();
    const { data: salesManagers, isLoading: isSalesManagersLoading } = useGetSalesManagersQuery(undefined, {
        selectFromResult: (response) => ({
            ...response,
            data: response.data?.map((user) => ({ label: user.full_name, value: user.id })) ?? [],
        }),
    });
    const [createFormRequest] = useCreateCompanyRequestMutation();

    const formik = useFormik<ICompanySubmitRequest>({
        initialValues: {
            companyName: '',
            companyType: null,
            country: '',
            salesManager: null,
        },
        validationSchema: RequestCompanySchema,
        onSubmit: async (values) => {
            try {
                await createFormRequest(values).unwrap();
            } catch (err) {
                handleError('Error submiting Company', err);
            }
        },
        enableReinitialize: true,
    });

    const { values, touched, errors, handleChange, handleBlur, handleSubmit, setFieldValue } = formik;

    const selectedCompanyType = useMemo(() => {
        return companyTypes?.find((type) => type.value === values?.companyType) ?? null;
    }, [companyTypes, values?.companyType]);

    const selectedCountry = useMemo(() => {
        return countries?.find((country) => country.value === values?.country) ?? null;
    }, [countries, values?.country]);

    const selectedSalesManager = useMemo(() => {
        return salesManagers?.find((manager) => manager.value === Number(values?.salesManager)) ?? null;
    }, [salesManagers, values?.salesManager]);

    const isFormInvalid = useMemo(() => {
        return Object.keys(errors).length > 0 || Object.keys(touched).length === 0;
    }, [errors, touched]);

    return (
        <div className="flex h-full w-full">
            <form className="flex flex-col gap-5 w-full" onSubmit={handleSubmit}>
                <div className="flex flex-wrap sm:gap-5 gap-3 justify-between relative">
                    <div className="mb-5 w-full">
                        <p className="font-medium mb-1 text-[#344054]">
                            Company name
                            <span className="text-red-500"> *</span>
                        </p>
                        <TextField
                            name="companyName"
                            className="!font-roboto"
                            fullWidth
                            color="primary"
                            placeholder="Name your Company"
                            value={values.companyName}
                            onChange={handleChange}
                            onBlur={handleBlur}
                        />
                    </div>
                    <div className="mb-5 w-full">
                        <p className="font-medium mb-1 text-[#344054]">
                            Company Type
                            <span className="text-red-500"> *</span>
                        </p>
                        <Autocomplete
                            renderInput={(params) => (
                                <TextField
                                    {...params}
                                    name="companyType"
                                    slotProps={{
                                        htmlInput: {
                                            ...params.inputProps,
                                        },
                                    }}
                                />
                            )}
                            options={companyTypes ?? []}
                            onChange={(_e, value) => {
                                setFieldValue('companyType', value?.value ?? '');
                            }}
                            onBlur={handleBlur}
                            value={selectedCompanyType}
                            fullWidth
                            loading={isCompanyTypesLoading}
                            renderOption={(props, option) => (
                                <CustomWidthTooltip title={option.reason && getReasonMessage(option.reason)}>
                                    <li
                                        {...props}
                                        style={{
                                            ...props.style,
                                            color: option.allowed ? 'inherit' : 'gray',
                                            backgroundColor: option.allowed ? 'inherit' : '#f0f0f0',
                                            cursor: option.allowed ? 'pointer' : 'not-allowed',
                                        }}
                                        onClick={option.allowed ? props.onClick : undefined}
                                    >
                                        <span style={{ pointerEvents: 'none' }}>{option.label}</span>
                                    </li>
                                </CustomWidthTooltip>
                            )}
                        />
                    </div>
                    <div className="mb-5 w-full">
                        <p className="font-medium mb-1 text-[#344054]">
                            Country
                            <span className="text-red-500"> *</span>
                        </p>
                        <Autocomplete
                            renderInput={(params) => <TextField {...params} name="country" />}
                            options={countries ?? []}
                            onChange={(_e, value) => {
                                setFieldValue('country', value?.value ?? '');
                            }}
                            onBlur={handleBlur}
                            value={selectedCountry}
                            fullWidth
                            loading={isCountriesLoading}
                        />
                    </div>
                    <div className="mb-5 w-full">
                        <p className="font-medium mb-1 text-[#344054]">
                            Sales Manager
                            <span className="text-red-500"> *</span>
                        </p>
                        <Autocomplete
                            renderInput={(params) => <TextField {...params} name="salesManager" />}
                            options={salesManagers ?? []}
                            onChange={(_e, value) => {
                                setFieldValue('salesManager', value?.value ?? '');
                            }}
                            onBlur={handleBlur}
                            value={selectedSalesManager}
                            fullWidth
                            loading={isSalesManagersLoading}
                        />
                    </div>
                </div>
                <Button type="submit" className="self-end bg-blue-500 text-white px-4 py-2" disabled={isFormInvalid}>
                    Send request
                </Button>
            </form>
        </div>
    );
};

export { OverviewForm };
